import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useGlobalContext } from "../../../Config.js";
import { ConfirmDialog} from "primereact/confirmdialog";
import TableSubproductos from "../components/TableSubproductosType.js";
import FormCrearSubproducto from "../components/FormCrearSubproductoType.js";
import FormEditarSubproducto from "../components/FormEditarSubproductoType.js";
import Loader from "../../../shared/ui/Loader.js";
import { Toast } from 'primereact/toast';
import {
  createSubproducto,
  updateTipoSubproducto,
  insertTarifaSubproducto,
} from "../../../services/SubproductoTypeService.js";
import useEmpresas from "../hooks/useEmpresas.js";
import useSubproductos from "../hooks/useSubproductos.js";
import { tieneCaracteresInvalidos, validarDescripcion } from "../../../utilities/textValidations.js";
import { existeSubproductoDuplicado } from "../utils/subproductoTypeValidations.js";

const MantenedorSubproductos = () => {
  const toast = useRef(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [displayDialog, setDisplayDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedSubproducto, setSelectedSubproducto] = useState(null);
  const [descripcion, setDescripcion] = useState("");
  const {baseUrl, headerText } = useGlobalContext();
  const idUsuario = sessionStorage.getItem("usuario_id");
  const [unidad, setUnidad] = useState("");
  const [tarifa, setTarifa] = useState("");
  const [incluido, setIncluido] = useState(false);

  const { empresa, setEmpresa, listaEmpresas, nombreEmpresa } = useEmpresas(baseUrl);
  const { subproductos, loading, setLoading, fetchData } = useSubproductos(
    baseUrl,
    empresa,
    nombreEmpresa
  );

  useEffect(() => {
    if (empresa) {
      setGlobalFilterValue('');
      fetchData();
    }
  }, [empresa, baseUrl, fetchData]);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
  };

  const [subproducto, setSubproducto] = useState({
    descripcion: ""
  });

  const validateInputs = () => {
    if (!subproducto.descripcion || !unidad) {
      alert("Todos los campos son obligatorios.");
      return false;
    }
    if (tieneCaracteresInvalidos(subproducto.descripcion)) {
      alert("La descripción no debe contener caracteres especiales");
      return false;
    }
    // GUARD: descripcion obligatoria y largo maximo 45
    const errorDescripcion = validarDescripcion(subproducto.descripcion);
    if (errorDescripcion) {
      alert(errorDescripcion);
      return false;
    }

    // Validación: no permitir crear subproductos con misma descripción y unidad
    if (existeSubproductoDuplicado(subproductos, subproducto.descripcion, unidad)) {
      alert("Ya existe un subproducto con esa descripción.");
      setLoading(false);
      return;
    }

    return true;
  };

  const handleCreateSubproducto = async () => {
    if (!validateInputs()) {
      return;
    }
    try{
      setLoading(true);

      const newSubproducto = {
        empresa: nombreEmpresa,
        descripcion: subproducto.descripcion,
        unidad: unidad,
        idUsuario: idUsuario,
        incluido: incluido
      };
      // Esperar el resultado y obtener el ID del subproducto creado
      const insertedSubproducto = await createSubproducto(baseUrl, newSubproducto, headerText);

      const newTarifaSubproducto = {
        idTipoSubproducto: insertedSubproducto.subproducto.id,
        idEmpresa: insertedSubproducto.subproducto.idEmpresa,
        tarifaBase: tarifa,
        idUsuario:idUsuario,
        moneda: "UF"
      };
      try {
        await insertTarifaSubproducto(baseUrl, newTarifaSubproducto);
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Subproducto creado exitosamente.' });
      } catch (error) {
        console.error("Error al crear tarifasubproducto:", error);
        toast.current.show({ severity: 'error', summary: 'Error', detail: error.message || "Error al crear subproducto" });
      }

      setSubproducto({descripcion: ""});
      setUnidad("");
      setTarifa("");
      setIncluido(false);
      return insertedSubproducto
    }catch (error) {
      console.error("Error al crear subproducto:", error);
      alert(error.message || "Error al crear subproducto");
      setLoading(false);
      return null;
    }finally{
      fetchData();
      setLoading(false);
      setDisplayDialog(false);
    }
  };

  // AUTOCOMPLETADO DE DIALOG EDITAR SUBPRODUCTO
  const handleEditSubproducto = (subproducto) => {
    setSelectedSubproducto(subproducto);
    setDescripcion(subproducto.descripcion);
    setIncluido(subproducto.incluido === true);
    setShowEditDialog(true);
  };

  const handleSaveSubproducto = async () => {
    // GUARD: descripcion obligatoria y largo maximo 45 (sin cambios si falla)
    const errorDescripcion = validarDescripcion(descripcion);
    if (errorDescripcion) {
      alert(errorDescripcion);
      return;
    }
    try {
      setLoading(true);
      const updatedSubproducto = {
        id: selectedSubproducto.id,
        descripcion: descripcion,
        idUsuario: idUsuario,
        incluido: incluido
      };
      await updateTipoSubproducto(baseUrl, updatedSubproducto, headerText);

      setSubproducto({ descripcion: ""});
      setShowEditDialog(false);
      toast.current.show({ severity: 'success', summary: 'Success', detail: 'Subproducto actualizado exitosamente.' });
      fetchData();
    } catch (error) {
      console.error("Error al actualizar el subproducto:", error);
      toast.current.show({ severity: 'error', summary: 'Error', detail: error.message || "Error al actualizar el subproducto" });
    }
  };

  //CABECERA
  const renderHeader = () => {
    return (
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
        <span className="p-input-icon-left" style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={(e) => {
              const upperCaseValue = e.target.value.toUpperCase();
              onGlobalFilterChange({ target: { value: upperCaseValue } });
            }}
            placeholder="Buscar por Descripción"
          />
        </span>
        <span className="p-input-icon-left ml-2" style={{display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
          {subproductos?.length} subproductos de {nombreEmpresa}
        </span>
      </div>
    );
  };
  const header = renderHeader();

  return (
    <div className="grid">
      <Toast ref={toast} />
      <div className="col-12">
        <div className="card">
          <h4>Gestión de Tipos de Subproductos</h4>
          <div className="p-field">
            <label htmlFor="empresa">Seleccione Empresa: </label>
            <Dropdown
              id="empresa"
              value={empresa}
              onChange={(e) => setEmpresa(e.value)}
              options={listaEmpresas}
              optionLabel="nombre"
              optionValue="id"
              placeholder="Seleccione Empresa"
              className="p-inputtext-sm"
              style={{ borderRadius: "4px" }}
            />
          </div>
        </div>
        {empresa && (
          <>
            {loading ? (
              <>
                <Loader />
              </>
            ) : (
              <>
                <div className="card">
                  <Button
                    label="Crear Tipo Subproducto"
                    icon="pi pi-plus"
                    className="p-button p-component mr-2 p-button-rounded p-button-primary mb-3"
                    onClick={() => setDisplayDialog(true)}
                    raised
                  />

                    <TableSubproductos
                      header={header}
                      subproductos={subproductos}
                      loading={loading}
                      globalFilterValue={globalFilterValue}
                      handleEditSubproducto={handleEditSubproducto}
                      nombreEmpresa={nombreEmpresa}
                    />

                    <FormCrearSubproducto
                      displayDialog={displayDialog}
                      setDisplayDialog={setDisplayDialog}
                      subproducto={subproducto}
                      setSubproducto={setSubproducto}
                      handleCreateSubproducto={handleCreateSubproducto}
                      baseUrl={baseUrl}
                      empresa={empresa}
                      nombreEmpresa={nombreEmpresa}
                      unidad={unidad}
                      setUnidad={setUnidad}
                      tarifa={tarifa}
                      setTarifa={setTarifa}
                      incluido={incluido}
                      setIncluido={setIncluido}
                    />

                    <FormEditarSubproducto
                      showEditDialog = {showEditDialog}
                      setShowEditDialog = {setShowEditDialog}
                      subproducto={subproducto}
                      setSubproducto={setSubproducto}
                      handleEditSubproducto={handleEditSubproducto}
                      baseUrl={baseUrl}
                      empresa={empresa}
                      nombreEmpresa={nombreEmpresa}
                      descripcion={descripcion}
                      setDescripcion={setDescripcion}
                      handleSaveSubproducto = {handleSaveSubproducto}
                      incluido={incluido}
                      setIncluido={setIncluido}
                    />

                    <ConfirmDialog/>

                </div>
              </>
            )}
          </>
        )}
      </div>

    </div>
  );
};
export default MantenedorSubproductos;
